import { useState } from 'react';
import { C, SERIF, mono } from '../theme';
import { Icon } from '../icons';
import { Pill } from '../components/Pill';
import { Loading, Empty } from '../components/States';
import { api } from '../api';
import type { TenancyRow } from '../api';
import { useAsync } from '../useAsync';
import { inr } from '../format';
import { unitDesc, rowPill } from './helpers';
import { AddTenancy } from './forms/AddTenancy';
import { SampleBadge } from '../components/SampleBadge';

export function OwnerRoster({
  propertyName,
  onOpen,
}: {
  propertyName?: string;
  onOpen: (tenancyId: string) => void;
}) {
  const { data, loading, error, reload } = useAsync<TenancyRow[]>(() => api.tenancies(), []);
  const [adding, setAdding] = useState(false);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 16 }}>
        <div>
          <h2 style={{ fontFamily: SERIF, fontSize: 22, margin: '0 0 4px', fontWeight: 600 }}>Tenancies</h2>
          <div style={{ color: C.muted, fontSize: 13.5 }}>
            {propertyName ?? 'Property'}
            {data ? ` · ${data.length} active` : ''}
          </div>
        </div>
        <button
          onClick={() => setAdding(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: C.accent, color: '#fff', border: 0, borderRadius: 10, padding: '10px 15px', fontWeight: 650, fontSize: 13.5, cursor: 'pointer', flex: 'none' }}
        >
          <Icon n="plus" size={15} color="#fff" /> Add tenancy
        </button>
      </div>

      {loading ? (
        <Loading label="Loading tenancies…" minHeight={360} />
      ) : error ? (
        <Empty icon="key" title="Tenancies unavailable" hint="We couldn't reach the tenancies list." minHeight={360}>
          <button onClick={reload} style={retryBtn}>Retry</button>
        </Empty>
      ) : !data || data.length === 0 ? (
        <Empty icon="key" title="No tenancies yet" hint="Add your first tenant to start tracking rent." minHeight={360}>
          <button onClick={() => setAdding(true)} style={{ marginTop: 4, background: C.accent, color: '#fff', border: 0, borderRadius: 9, padding: '9px 16px', fontWeight: 650, fontSize: 13, cursor: 'pointer' }}>
            Add tenancy
          </button>
        </Empty>
      ) : (
        <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: 13, overflow: 'hidden' }}>
          {data.map((t, i) => {
            const p = rowPill(t.status, t.dueInDays);
            return (
              <div key={t.id} onClick={() => onOpen(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: i < data.length - 1 ? `1px solid ${C.line2}` : 0, cursor: 'pointer' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: C.soft, color: C.accent2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon n="key" size={18} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b style={{ color: C.ink, fontWeight: 600, fontSize: 14.5, display: 'block' }}>{t.unitLabel} · {t.tenantName}<SampleBadge when={t.isSample} /></b>
                  <small style={{ color: C.muted, fontSize: 12.5 }}>{unitDesc(t)}</small>
                </div>
                <div style={{ ...mono(), color: C.ink, fontWeight: 600 }}>
                  {inr(t.rentAmount)}
                  <span style={{ color: C.muted, fontWeight: 400 }}>/mo</span>
                </div>
                <div style={{ width: 80, textAlign: 'right' }}><Pill kind={p.kind}>{p.label}</Pill></div>
                <Icon n="chev" size={16} color={C.muted} />
              </div>
            );
          })}
        </div>
      )}

      {adding && <AddTenancy onClose={() => setAdding(false)} onCreated={reload} />}
    </>
  );
}

const retryBtn: React.CSSProperties = {
  marginTop: 4,
  border: `1px solid ${C.line}`,
  background: C.card,
  color: C.accent,
  fontWeight: 600,
  fontSize: 13,
  padding: '8px 16px',
  borderRadius: 9,
  cursor: 'pointer',
};
